import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../auth/AuthContext";
import { Button } from "./ui/button";
import { Sparkles, AlertCircle, Lock, Mic } from "lucide-react";

export default function SubscriptionBanner() {
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/billing/me`);
        setInfo(r.data);
      } catch (e) {}
    })();
  }, [user]);

  if (!info) return null;
  const sub = info.subscription || {};
  const expiresAt = sub.expires_at ? new Date(sub.expires_at) : null;
  const daysLeft = expiresAt ? Math.max(0, Math.ceil((expiresAt - new Date()) / 86400000)) : 0;
  const hoursLeft = expiresAt ? Math.max(0, Math.ceil((expiresAt - new Date()) / 3600000)) : 0;

  // Active paid subscriber on Basic — soft nudge to Advanced (voice-first benefit)
  if (sub.is_active && !sub.is_trial && (sub.plan === "basic" || sub.plan === "basic_annual")) {
    return (
      <div
        data-testid="subscription-banner-basic-upgrade"
        className="mb-6 flex items-center gap-3 rounded-xl bg-[#e7edf5] border border-[#b9c8db] p-4"
      >
        <div className="w-9 h-9 rounded-lg bg-white/70 flex items-center justify-center shrink-0">
          <Mic size={15} className="text-[#486a8f]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-body text-[13px] font-medium text-[#0f1724]">
            You're using Basic
          </p>
          <p className="font-body text-[12px] text-[#4b5563]">
            Upgrade for voice coaching, persistent memory, deeper personalization, and weekly insights.
          </p>
        </div>
        <Button
          data-testid="subscription-banner-upgrade-btn"
          onClick={() => navigate("/pricing")}
          className="h-9 px-4 rounded-full bg-[#486a8f] hover:bg-[#3a5a7c] text-white text-[12px] font-medium shrink-0"
        >
          Upgrade
        </Button>
      </div>
    );
  }

  if (sub.is_active && !sub.is_trial) return null;

  // Trialing — show countdown banner with urgency styling that ramps up
  if (sub.is_trial && sub.is_active) {
    const urgent = daysLeft <= 1;
    return (
      <div
        data-testid="subscription-banner-trial"
        className={`mb-6 flex items-center gap-3 rounded-xl border p-4 ${
          urgent
            ? "bg-[#f5e6e6] border-[#e8c8c8]"
            : "bg-[#fbf5e8] border-[#ead7a8]"
        }`}
      >
        <div
          className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
            urgent ? "bg-[#b94e4e]/15" : "bg-[#e9d8a6]/50"
          }`}
        >
          {urgent ? (
            <AlertCircle size={18} className="text-[#b94e4e]" />
          ) : (
            <Sparkles size={18} className="text-[#a07c2b]" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-body text-[13px] font-semibold text-[#0f1724]">
            {urgent
              ? `Trial ends in ${hoursLeft} hour${hoursLeft === 1 ? "" : "s"}`
              : `${daysLeft} day${daysLeft === 1 ? "" : "s"} left in your free trial`}
          </p>
          <p className="font-body text-[12px] text-[#4b5563]">
            {urgent
              ? "Pick a plan to keep voice, memory, and weekly insights."
              : "Pick a plan to keep voice coaching, deeper memory, and weekly insights."}
          </p>
        </div>
        <Button
          data-testid="subscription-banner-choose-plan-btn"
          onClick={() => navigate("/pricing")}
          className={`h-9 px-4 rounded-full text-white text-[12px] font-medium shrink-0 ${
            urgent
              ? "bg-[#b94e4e] hover:bg-[#a33f3f]"
              : "bg-[#486a8f] hover:bg-[#3a5a7c]"
          }`}
        >
          {urgent ? "Lock in plan" : "Choose plan"}
        </Button>
      </div>
    );
  }

  // Expired / none
  return (
    <div
      data-testid="subscription-banner-expired"
      className="mb-6 flex items-center gap-3 rounded-xl bg-[#f5e6e6] border border-[#e8c8c8] p-4"
    >
      <div className="w-9 h-9 rounded-lg bg-[#b94e4e]/15 flex items-center justify-center shrink-0">
        <Lock size={15} className="text-[#b94e4e]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-body text-[13px] font-medium text-[#0f1724]">
          Your trial has ended.
        </p>
        <p className="font-body text-[12px] text-[#4b5563]">
          Pick a plan to keep voice coaching, deeper memory, and weekly insights.
        </p>
      </div>
      <Button
        data-testid="subscription-banner-reactivate-btn"
        onClick={() => navigate("/pricing")}
        className="h-9 px-4 rounded-full bg-[#b94e4e] hover:bg-[#a33f3f] text-white text-[12px] font-medium shrink-0"
      >
        Reactivate
      </Button>
    </div>
  );
}
